"use client";

import React, { createContext, ReactNode, useContext, useMemo } from "react";
import { usePathname, useSearchParams } from "next/navigation";

// The url-building half lives in a React-free module so the node --test guard
// can import it; re-exported here so consumers have a single import path.
export { tradeReturnUrlForCard } from "modules/common/tradeReturnUrl";

interface TradeReturnSurfaceValue {
  filterKey: string;
  /** Where the visitor should land, filtered to the card they clicked. */
  baseUrl: string;
}

/**
 * Where an anonymous visitor should land after signing up or in from a card's
 * "Trade" button.
 *
 * Context rather than props: two independent chains reach that button (grid
 * cells via TradeItemGridCell, table rows via the column hooks) and neither can
 * thread a prop through. Context rather than a store: a page can show more than
 * one trade surface at once — the user profile renders both a Haves and a Wants
 * section, each of which must return the visitor to a *different* list — and a
 * single global slot has no correct value to hold in that case.
 *
 * No provider is the safe state: the button keeps its current behaviour and the
 * visitor returns unfiltered.
 */
const TradeReturnContext = createContext<TradeReturnSurfaceValue | undefined>(
  undefined
);

export function useTradeReturnSurface(): TradeReturnSurfaceValue | undefined {
  return useContext(TradeReturnContext);
}

interface TradeReturnSurfaceProps {
  /** The surface's own nuqs url key for its free-text card filter. */
  filterKey: string | undefined;
  /**
   * Where to send the visitor back to. Omit it on a page that honours
   * `filterKey` in its own URL and the live URL is used, so the visitor's
   * tab/sort/facets survive the round trip. Pass it explicitly when the
   * rendering page does *not* honour `filterKey` — the profile overview lists
   * cards but takes no search params, so it points at /users/<name>/haves.
   */
  baseUrl?: string;
  children: ReactNode;
}

/**
 * Splitting on `baseUrl` rather than branching inside one component is
 * deliberate: useSearchParams() opts its whole subtree out of static rendering
 * unless a <Suspense> boundary catches it, and a caller that already knows its
 * return url must not pay that. /users/[username] is the case that matters — it
 * calls neither cookies() nor auth(), so it is only saved from a whole-page CSR
 * bailout by the Suspense boundaries around its RecentTrades sections. Taking
 * this branch means that boundary is no longer load-bearing for this feature.
 */
export function TradeReturnSurface({
  filterKey,
  baseUrl,
  children,
}: TradeReturnSurfaceProps) {
  return baseUrl === undefined ? (
    <LiveUrlTradeReturnSurface filterKey={filterKey}>
      {children}
    </LiveUrlTradeReturnSurface>
  ) : (
    <ResolvedTradeReturnSurface filterKey={filterKey} baseUrl={baseUrl}>
      {children}
    </ResolvedTradeReturnSurface>
  );
}

function LiveUrlTradeReturnSurface({
  filterKey,
  children,
}: Omit<TradeReturnSurfaceProps, "baseUrl">) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams.toString();
  const baseUrl = search ? `${pathname}?${search}` : pathname;

  return (
    <ResolvedTradeReturnSurface filterKey={filterKey} baseUrl={baseUrl}>
      {children}
    </ResolvedTradeReturnSurface>
  );
}

function ResolvedTradeReturnSurface({
  filterKey,
  baseUrl,
  children,
}: Omit<TradeReturnSurfaceProps, "baseUrl"> & { baseUrl: string }) {
  // A plain value on purpose: the url only changes when the filter/sort/tab
  // does, and the rows re-render on those anyway. A ref-backed getter would
  // save those re-renders but freezes the first url under React.memo or the
  // compiler, since neither the context value nor the card name changes.
  const value = useMemo(
    () => (filterKey ? { filterKey, baseUrl } : undefined),
    [filterKey, baseUrl]
  );

  return (
    <TradeReturnContext.Provider value={value}>
      {children}
    </TradeReturnContext.Provider>
  );
}
